import type { ReactNode } from "react"

import { Button } from "@coval/ui"
import type { LucideIcon } from "lucide-react"

import { cn } from "@/utils/cn"

interface EmptyStateActionsProps {
  docsHref?: string
  apiDocsHref?: string
  children?: ReactNode
}

export function EmptyStateActions({
  docsHref,
  apiDocsHref,
  children,
}: EmptyStateActionsProps) {
  return (
    <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
      {docsHref && (
        <Button variant="secondary" asChild>
          <a href={docsHref} target="_blank" rel="noopener noreferrer">
            Docs
          </a>
        </Button>
      )}
      {apiDocsHref && (
        <Button variant="secondary" asChild>
          <a href={apiDocsHref} target="_blank" rel="noopener noreferrer">
            API Docs
          </a>
        </Button>
      )}
      {children}
    </div>
  )
}

interface EmptyStateCTAProps extends EmptyStateActionsProps {
  icon?: LucideIcon
  title: string
  description: string
  className?: string
  "data-testid"?: string
}

export function EmptyStateCTA({
  icon: Icon,
  title,
  description,
  docsHref,
  apiDocsHref,
  children,
  className,
  "data-testid": testId,
}: EmptyStateCTAProps) {
  return (
    <div
      className={cn(
        "flex h-full flex-1 flex-col items-center justify-center gap-2 text-center",
        className,
      )}
      data-testid={testId}
    >
      {Icon && (
        <Icon
          className="size-9 text-coval-text-muted/50"
          strokeWidth={1.5}
          aria-hidden="true"
        />
      )}
      <h2 className="mb-0 text-lg font-semibold text-coval-text">{title}</h2>
      <span className="max-w-sm text-sm text-coval-text-muted">
        {description}
      </span>
      {(docsHref || apiDocsHref || children) && (
        <div className="mt-2 max-w-full">
          <EmptyStateActions docsHref={docsHref} apiDocsHref={apiDocsHref}>
            {children}
          </EmptyStateActions>
        </div>
      )}
    </div>
  )
}

interface EmptyFilteredStateProps {
  title: string
  onReset?: () => void
  resetLabel?: "Clear search" | "Clear filters"
  "data-testid"?: string
}

export function EmptyFilteredState({
  title,
  onReset,
  resetLabel = "Clear filters",
  "data-testid": testId,
}: EmptyFilteredStateProps) {
  return (
    <EmptyStateCTA
      title={title}
      description="Try adjusting or clearing your search and filters."
      data-testid={testId}
    >
      {onReset && (
        <Button variant="secondary" onClick={onReset}>
          {resetLabel}
        </Button>
      )}
    </EmptyStateCTA>
  )
}
